import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ApiError, OpenAPI } from '@wisecure/api-client';
import { useAuthStore } from '@/stores';

function is401(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

function unwrapDataEnvelope(response: import('axios').AxiosResponse) {
  const d = response.data;
  if (
    d != null &&
    typeof d === 'object' &&
    !Array.isArray(d) &&
    'data' in d &&
    Object.keys(d).length === 1
  ) {
    response.data = d.data;
  }
  return response;
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
}

const refreshClient = axios.create();
refreshClient.interceptors.response.use(unwrapDataEnvelope);

axios.interceptors.response.use(unwrapDataEnvelope);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as (import('axios').AxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest._retry = true;
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${token}` };
        return axios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await refreshClient.post<{
        accessToken: string;
        expiresIn: number;
      }>(`${OpenAPI.BASE}/api/auth/refresh`, {}, { withCredentials: true });

      const user = useAuthStore.getState().user;
      if (!user) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      useAuthStore.getState().login(data.accessToken, user);

      processQueue(null, data.accessToken);

      originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${data.accessToken}` };
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error) => {
        if (is401(error)) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      onError: (error) => {
        if (is401(error)) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
      },
    },
  },
});
